import { useIntl } from 'react-intl';
import { IconTooltip, LoadingSkeleton, useQueryFetch } from 'sonata-react-components';

import { type DeviatedLineItemsCountRequestBody, getDeviatedLineItemsCount } from '@/services/lineItem.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';

import '../Progress/progress.scss';
import './deviatedLineItemsCount.scss';

type Props = {
  cacheSeed: string;
  filters: DeviatedLineItemsCountRequestBody;
};

const DeviatedLineItemsCount = ({ cacheSeed, filters }: Readonly<Props>) => {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const { id: userId = '' } = user.entity;

  const { data, isFetching, isLoading } = useQueryFetch(
    [userId, 'deviatedLineItemsCount', filters, cacheSeed],
    // react-query aborts the signal once this key is superseded (filter change) or unmounted
    ({ signal }) => getDeviatedLineItemsCount(userId, filters, signal),
    { cacheTime: 30 * 1000 }
  );

  if (isFetching || isLoading) {
    return <LoadingSkeleton height="100%" width="100%" />;
  }

  return (
    <div className="progress deviatedLineItemsCount" id="deviatedLineItem_multiflight_count">
      <div className="progress__numbersSection">
        <i className="progress__numbersSectionIcon fa-duotone fa-solid fa-triangle-exclamation" />
        <div className="progress__numbersSectionMain">
          <div className="progress__numbersSectionTopRow" style={{ gap: 'var(--spacing-8)' }}>
            <span className="progress__numbersSectionTotalBudgetAmount">
              {formatMessage(
                {
                  defaultMessage: '{count, plural, one {# deviated line item} other {# deviated line items}}',
                  id: 'summaryBar.deviatedLineItemsCount.value'
                },
                { count: data?.totalItems ?? 0 }
              )}
            </span>
            <IconTooltip
              effect="solid"
              event="mouseenter"
              icon="fa-regular fa-circle-info"
              iconColor="var(--color-sonata-primary-500) !important"
              place="bottom"
            >
              {formatMessage({
                defaultMessage: 'Total line items with the selected deviated number and filters applied.',
                id: 'summaryBar.deviatedLineItemsCount.tooltip'
              })}
            </IconTooltip>
          </div>
          <span className="progress__numbersSectionTotalBudgetSubtitle">
            {formatMessage({
              defaultMessage: 'Delivery Status',
              id: 'summaryBar.deviatedLineItemsCount.label'
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

DeviatedLineItemsCount.displayName = 'DeviatedLineItemsCount';

export default DeviatedLineItemsCount;
